/**
 * OAuth Service for Google and Apple Sign-In
 *
 * Handles native sign-in on iOS (Apple only) and web OAuth fallback
 * Uses Supabase for session management
 *
 * IMPORTANT: For Apple Sign-In to work, the Apple provider must be enabled
 * in Supabase Dashboard (Authentication > Providers > Apple) with:
 * - Service ID (Client ID)
 * - Team ID
 * - Key ID
 * - Private Key (.p8 file contents)
 *
 * See: https://supabase.com/docs/guides/auth/social-login/auth-apple
 */

import { Capacitor } from '@capacitor/core';
import {
  SignInWithApple,
  type SignInWithAppleResponse,
  type SignInWithAppleOptions,
} from '@capacitor-community/apple-sign-in';
import type { User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { getAuthRedirectUrl } from '@/lib/capacitor';

// Platform detection
const isIOS = Capacitor.getPlatform() === 'ios';
const isAndroid = Capacitor.getPlatform() === 'android';

// Feature flags - set to 'true' to enable OAuth providers
const GOOGLE_AUTH_ENABLED = import.meta.env.VITE_ENABLE_GOOGLE_AUTH === 'true';
const APPLE_AUTH_ENABLED = import.meta.env.VITE_ENABLE_APPLE_AUTH === 'true';

/**
 * Initialize OAuth plugins
 * Call this once when the app starts
 */
export async function initOAuth(): Promise<void> {
  // No initialization needed - Apple Sign-In plugin doesn't require init
  // Google uses web OAuth flow on all platforms
}

/**
 * Sign in with Google
 * Uses Supabase OAuth redirect on all platforms
 */
export async function signInWithGoogle(): Promise<{
  success: boolean;
  error?: string;
}> {
  try {
    // Use Supabase OAuth redirect on all platforms
    // Native apps use custom URL scheme (parentpath://auth/callback)
    // Web uses window.location.origin
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: getAuthRedirectUrl('/auth/callback'),
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        },
      },
    });

    if (error) {
      console.error('Supabase Google OAuth error:', error);
      return { success: false, error: error.message };
    }

    // Redirect will happen automatically
    return { success: true };
  } catch (error) {
    console.error('Google sign-in error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Google sign-in failed',
    };
  }
}

/**
 * Namen aus dem nativen Apple-Sign-In nachtragen.
 *
 * Apple sendet givenName/familyName ausschliesslich bei der ERSTEN
 * Autorisierung eines Nutzers - danach nie wieder, auch nicht nach einer
 * Neuinstallation. Das Apple-ID-Token enthaelt den Namen ueberhaupt nicht,
 * und signInWithIdToken() nimmt keine Metadaten entgegen. Wer den Namen
 * hier nicht wegschreibt, hat ihn dauerhaft verloren.
 *
 * Geschrieben wird an beiden Stellen: in die Auth-Metadaten (die UI liest
 * user_metadata.display_name) und nach profiles. Der Trigger
 * handle_new_user ist als AFTER INSERT laengst gelaufen, bevor updateUser
 * die Metadaten aendert, und es gibt keinen Trigger auf UPDATE, der das
 * nachzieht - profiles muss also separat beschrieben werden. Zu diesem
 * Zeitpunkt existiert eine Session, beide Writes laufen als authenticated
 * und fallen unter profiles_update_own.
 *
 * Die Metadaten werden zuerst geschrieben, und zwar aus dem bereits
 * vorliegenden User-Objekt ohne zusaetzlichen Read. Scheitert danach der
 * profiles-Teil, ist der Name wenigstens in raw_user_meta_data gesichert
 * und laesst sich spaeter nachziehen - bei einem Wert, den Apple nur ein
 * einziges Mal liefert, ist das der Unterschied zwischen "spaeter
 * reparierbar" und "endgueltig weg".
 *
 * Vorhandene Namen werden nie ueberschrieben. Verknuepft jemand Apple mit
 * einem bestehenden Konto, gewinnt der selbst eingetragene Name.
 */
async function persistAppleName(
  user: User,
  givenName: string | null,
  familyName: string | null,
): Promise<void> {
  const firstName = givenName?.trim() || '';
  const lastName = familyName?.trim() || '';

  // Apple liefert bei jeder Folgeanmeldung null - dann gibt es nichts zu tun.
  if (!firstName && !lastName) return;

  const displayName = [firstName, lastName].filter(Boolean).join(' ');

  try {
    const meta = (user.user_metadata ?? {}) as Record<string, unknown>;
    const isBlank = (value: unknown) =>
      typeof value !== 'string' || value.trim() === '';

    const metaUpdates: Record<string, string> = {};
    if (isBlank(meta.display_name)) metaUpdates.display_name = displayName;
    if (firstName && isBlank(meta.first_name)) metaUpdates.first_name = firstName;
    if (lastName && isBlank(meta.last_name)) metaUpdates.last_name = lastName;

    if (Object.keys(metaUpdates).length > 0) {
      const { error: metaError } = await supabase.auth.updateUser({
        data: metaUpdates,
      });

      if (metaError) {
        console.error('Error writing Apple name to user metadata:', metaError);
      }
    }

    const { data: profile, error: readError } = await supabase
      .from('profiles')
      .select('display_name, first_name, last_name, email')
      .eq('id', user.id)
      .single();

    if (readError) {
      console.error('Could not read profile for Apple name backfill:', readError);
      return;
    }

    // handle_new_user setzt als Anzeigename den E-Mail-Lokalteil, wenn kein
    // Name in den Metadaten steht. Genau dieser Platzhalter darf weichen,
    // ein echter Name nicht.
    const emailLocalPart = profile?.email?.split('@')[0] ?? '';
    const displayNameIsPlaceholder =
      isBlank(profile?.display_name) || profile?.display_name === emailLocalPart;

    const profileUpdates: Record<string, string> = {};
    if (displayNameIsPlaceholder) profileUpdates.display_name = displayName;
    if (firstName && isBlank(profile?.first_name)) profileUpdates.first_name = firstName;
    if (lastName && isBlank(profile?.last_name)) profileUpdates.last_name = lastName;

    if (Object.keys(profileUpdates).length === 0) return;

    const { error: updateError } = await supabase
      .from('profiles')
      .update(profileUpdates)
      .eq('id', user.id);

    if (updateError) {
      console.error('Error writing Apple name to profile:', updateError);
    }
  } catch (err) {
    // Der Name ist ein Komfortdetail - er darf die Anmeldung nicht kippen.
    console.error('Unexpected error persisting Apple name:', err);
  }
}

/**
 * Sign in with Apple
 * - iOS: Uses native Sign in with Apple
 * - Web/Android: Uses Supabase OAuth redirect
 */
export async function signInWithApple(): Promise<{
  success: boolean;
  error?: string;
}> {
  try {
    if (isIOS) {
      // Native Apple Sign-In on iOS
      // Generate raw nonce for Supabase verification
      const rawNonce = crypto.randomUUID();

      // Hash the nonce with SHA256 for Apple
      // Apple expects the HASHED nonce, Supabase expects the RAW nonce
      const encoder = new TextEncoder();
      const data = encoder.encode(rawNonce);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashedNonce = hashArray
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');

      const options: SignInWithAppleOptions = {
        clientId: 'de.parentpath.app', // Bundle ID for native iOS
        redirectURI: 'https://parentpath.de', // Required by types but ignored for native
        scopes: 'email name',
        state: crypto.randomUUID(),
        nonce: hashedNonce, // Send HASHED nonce to Apple
      };

      console.log('Starting native Apple Sign-In, rawNonce:', rawNonce);

      const result: SignInWithAppleResponse =
        await SignInWithApple.authorize(options);

      console.log('Apple Sign-In result received');

      if (!result.response?.identityToken) {
        return { success: false, error: 'No identity token received from Apple' };
      }

      // Exchange Apple ID token with Supabase
      // Send the RAW (unhashed) nonce - Supabase will hash it and compare with token
      // Nicht "data" - der Name ist im Nonce-Hashing oben schon vergeben.
      const { data: signInData, error } = await supabase.auth.signInWithIdToken({
        provider: 'apple',
        token: result.response.identityToken,
        nonce: rawNonce, // Send RAW nonce to Supabase
      });

      if (error) {
        console.error('Supabase Apple sign-in error:', error);
        // Handle specific error when Apple provider is not enabled in Supabase
        if (error.message.includes('is not enabled') || error.message.includes('issuer')) {
          return {
            success: false,
            error: 'Apple-Anmeldung ist derzeit nicht verfügbar. Bitte versuche es später erneut.',
          };
        }
        return { success: false, error: error.message };
      }

      // Einzige Gelegenheit, den Namen zu sichern - siehe persistAppleName.
      if (signInData.user) {
        await persistAppleName(
          signInData.user,
          result.response.givenName,
          result.response.familyName,
        );
      }

      return { success: true };
    } else {
      // Web/Android fallback - use Supabase OAuth redirect
      // Native apps use custom URL scheme, web uses window.location.origin
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'apple',
        options: {
          redirectTo: getAuthRedirectUrl('/auth/callback'),
        },
      });

      if (error) {
        console.error('Supabase Apple OAuth error:', error);
        // Handle specific error when Apple provider is not enabled in Supabase
        if (error.message.includes('is not enabled') || error.message.includes('issuer')) {
          return {
            success: false,
            error: 'Apple-Anmeldung ist derzeit nicht verfügbar. Bitte versuche es später erneut.',
          };
        }
        return { success: false, error: error.message };
      }

      // Redirect will happen automatically
      return { success: true };
    }
  } catch (error) {
    console.error('Apple sign-in error:', error);

    // Handle user cancellation
    if (
      error instanceof Error &&
      (error.message.includes('cancelled') ||
        error.message.includes('canceled'))
    ) {
      return { success: false, error: 'Sign-in cancelled' };
    }

    return {
      success: false,
      error: error instanceof Error ? error.message : 'Apple sign-in failed',
    };
  }
}

/**
 * Sign out from OAuth providers
 */
export async function signOutOAuth(): Promise<void> {
  try {
    // Sign out from Supabase
    await supabase.auth.signOut();
  } catch (error) {
    console.error('OAuth sign-out error:', error);
  }
}

/**
 * Check if Apple Sign-In is available and enabled
 * Returns true if enabled via env var AND on iOS or web (not Android)
 */
export function isAppleSignInAvailable(): boolean {
  if (!APPLE_AUTH_ENABLED) return false;
  return isIOS || !isAndroid;
}

/**
 * Check if Google Sign-In is available and enabled
 * Returns true if enabled via env var
 */
export function isGoogleSignInAvailable(): boolean {
  return GOOGLE_AUTH_ENABLED;
}

/**
 * Get the current platform for conditional UI
 */
export function getOAuthPlatform(): 'ios' | 'android' | 'web' {
  if (isIOS) return 'ios';
  if (isAndroid) return 'android';
  return 'web';
}
